import { useSurfacePreferenceProperty } from "@/components/preferences/usePreferences.tsx";
import type { KeyboardShortcutId } from "@/types/keyboardShortcuts.ts";
import { type ShortcutDef, SHORTCUTS } from "./shortcuts.ts";

/**
 * The user's stored overrides, one nullable list per shortcut id. `null` (or
 * a missing id) means the registry default applies; `[]` disables the
 * shortcut; anything else replaces the default keys outright, alternates
 * included.
 */
export type ShortcutBindings = Partial<Record<KeyboardShortcutId, string[] | null>>;

/** The stored map as the preference surface holds it: every id present, `null` when unset. */
export type StoredShortcutBindings = Record<KeyboardShortcutId, string[] | null>;

/**
 * The keys a shortcut actually responds to: the user's binding when one is
 * stored, else the registry default. A `fixed` shortcut ignores bindings, since
 * its handler does not read the registry and a stored override would be a lie.
 */
export const resolveShortcutKeys = (def: ShortcutDef, bindings: ShortcutBindings): string[] => {
  if (def.fixed) return def.keys;
  return bindings[def.id] ?? def.keys;
};

/** True when the user has stored their own keys (or a disable) for this shortcut. */
export const isShortcutCustomized = (def: ShortcutDef, bindings: ShortcutBindings): boolean =>
  !def.fixed && bindings[def.id] != null;

/** The stored overrides for every shortcut, from the keyboard preference surface. */
export const useShortcutBindings = (): StoredShortcutBindings =>
  useSurfacePreferenceProperty("keyboard", "bindings");

/** The effective keys for one shortcut id. */
export const useEffectiveShortcutKeys = (id: KeyboardShortcutId): string[] =>
  resolveShortcutKeys(SHORTCUTS[id], useShortcutBindings());
